import { yupResolver } from '@hookform/resolvers/yup'
import { filter, find, map } from 'lodash-es'
import { FC, useEffect, useState } from 'react'
import {
  Control,
  FieldArrayWithId,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
  useFieldArray,
  useForm,
} from 'react-hook-form'
import * as yup from 'yup'

import { PreviewTemplate } from './PreviewTemplate'
import Loader from '../../../../common/components/Loader'
import {
  EmailTemplateVelocity,
  ITemplates,
  Languages,
  Template,
  TemplateType,
  Variable,
} from '../../../../common/models'
import FormGroup from '../../../../core/components/FormGroup'
import { HtmlEditor } from '../../../../core/components/HtmlEditor'
import { useCheckPermission } from '../../../../core/hooks/use-check-permission'
import {
  deleteEmailTemplateVelocity,
  getAllEmailTemplates,
  getEmailTemplateById,
  getEmailTemplateVelocity,
  getPreviews,
  updateEmailTemplateById,
  updateEmailTemplateVelocity,
} from '../../../../core/services/email-templates'
import { MessageBox } from '../../messages/MessageBox'
import { Button, Card, Checkbox } from '@/components/ui'
import SelectField from '@/components/select-field'
import type { SelectOptionGroup } from '@/components/select-field'
import { Icon } from '@/common/components/Icon'

const A4_WIDTH_CM = 21
const PX_PER_CM = 96 / 2.54
const A4_WIDTH_PX = Math.round(A4_WIDTH_CM * PX_PER_CM)

const measureA4HeightCm = (html: string): Promise<number> =>
  new Promise((resolve) => {
    const iframe = document.createElement('iframe')
    iframe.setAttribute('aria-hidden', 'true')
    iframe.style.position = 'fixed'
    iframe.style.left = '-99999px'
    iframe.style.top = '0'
    iframe.style.width = `${A4_WIDTH_PX}px`
    iframe.style.height = '0'
    iframe.style.border = '0'
    iframe.style.visibility = 'hidden'
    iframe.srcdoc = html
    iframe.onload = () => {
      const doc = iframe.contentDocument
      const heightPx = doc?.documentElement?.scrollHeight ?? 0
      iframe.remove()
      resolve(heightPx / PX_PER_CM)
    }
    document.body.appendChild(iframe)
  })

interface IFormValues {
  id: string
  name: string
  category: string
  type: TemplateType
  texts: {
    id: string
    key: string
    optional: boolean
    displayable: boolean
    translations?: {
      id: string
      locale: string
      text: string
    }[]
  }[]
}

type EmailTemplateItemProps = {
  control: Control<IFormValues>
  field: FieldArrayWithId<IFormValues, 'texts', 'id'>
  index: number
  watch: UseFormWatch<IFormValues>
  setValue: UseFormSetValue<IFormValues>
  errors: FieldErrors<IFormValues>
  allLanguages: Languages[]
  variables: Variable[]
  disabled: boolean
}

const EmailTemplateItem: FC<EmailTemplateItemProps> = ({
  control,
  errors,
  field,
  index,
  watch,
  setValue,
  allLanguages,
  variables,
  disabled,
}) => {
  const { fields: contentFields } = useFieldArray({
    control,
    name: `texts.${index}.translations`,
  })

  const visible = watch(`texts.${index}.displayable`)
  const optional = watch(`texts.${index}.optional`)

  function camelCaseToWords(s: string) {
    const result = s.replace(/([A-Z])/g, ' $1')
    return result.charAt(0).toUpperCase() + result.slice(1)
  }

  return (
    <div className="bg-gradient-4 p-5 rounded-2xl border border-monochrome-120 mt-4">
      <h3 className="text-title-xs text-monochrome-1 mb-4">
        {camelCaseToWords(field.key)}
      </h3>
      {optional && (
        <FormGroup
          label="Visible"
          direction="row"
          labelClassName="whitespace-nowrap"
        >
          <Checkbox
            checked={field.displayable}
            onCheckedChange={(checked) => {
              setValue(`texts.${index}.displayable`, Boolean(checked))
            }}
            disabled={disabled}
          />
        </FormGroup>
      )}
      {visible &&
        contentFields.map((contentField, contentIndex) => {
          const html = watch(`texts.${index}.translations.${contentIndex}.text`)
          return (
            <Card
              className="border border-monochrome-3-60 p-4 mb-4"
              key={contentField.id}
            >
              <div className="flex justify-between items-center">
                <h5 className="text-subtitle-m text-monochrome-1">
                  Language:{' '}
                  <span className="text-accent-1">
                    {
                      find(
                        allLanguages,
                        (cont) => contentField.locale === cont.locale,
                      )?.name
                    }
                  </span>
                </h5>
              </div>
              <HtmlEditor
                label={'Translation'}
                control={control}
                variables={variables}
                defaultCustomHTML={false}
                isError={
                  !!errors?.texts?.[index]?.translations?.[contentIndex]?.text
                    ?.message
                }
                name={`texts.${index}.translations.${contentIndex}.text`}
                value={html || ''}
                onChange={(data) => {
                  setValue(
                    `texts.${index}.translations.${contentIndex}.text`,
                    data,
                  )
                }}
                disabled={disabled}
                index={contentIndex}
              />
            </Card>
          )
        })}
    </div>
  )
}

type CustomHtmlSectionProps = {
  title: string
  fieldName: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>
  allLanguages: Languages[]
  variables: Variable[]
  disabled: boolean
  valueByLocale: Record<string, string>
  setValueForLocale: (locale: string, value: string) => void
  onUseDefault?: (locale: string) => void
  showPreview?: boolean
  showLegend?: boolean
  templateId?: string
  subjectByLocale?: Record<string, string>
  pdfPerTicketHeightByLocale?: Record<string, number>
}

const CustomHtmlSection: FC<CustomHtmlSectionProps> = ({
  title,
  fieldName,
  control,
  allLanguages,
  variables,
  disabled,
  valueByLocale,
  setValueForLocale,
  onUseDefault,
  showPreview = false,
  showLegend = false,
  templateId,
  subjectByLocale,
  pdfPerTicketHeightByLocale,
}) => {
  return (
    <div className="bg-gradient-4 p-5 rounded-2xl border border-monochrome-120 mt-4">
      <h3 className="text-title-xs text-monochrome-1 mb-4">{title}</h3>
      {allLanguages.map((language, contentIndex) => {
        const html = valueByLocale[language.locale] || ''
        return (
          <div className="mb-4" key={language.locale}>
            {onUseDefault && (
              <div className="flex justify-end mb-2">
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  onClick={() => onUseDefault(language.locale)}
                  disabled={disabled}
                >
                  Use Default
                </Button>
              </div>
            )}
            <HtmlEditor
              label={
                <div>
                  <h5 className="text-subtitle-m text-monochrome-1">
                    Language:{' '}
                    <span className="text-accent-1">{language.name}</span>
                  </h5>
                  <span className="text-body-medium text-monochrome-2">
                    Translation
                  </span>
                </div>
              }
              control={control}
              variables={variables}
              defaultCustomHTML={false}
              isError={false}
              name={`${fieldName}-${language.locale}`}
              value={html}
              onChange={(data) => setValueForLocale(language.locale, data)}
              disabled={disabled}
              index={contentIndex}
              showPreview={showPreview}
              textAreaHeight={showLegend ? 35 : 9}
              showLegend={showLegend}
              templateId={templateId}
              previewLocale={language.locale}
              previewSubject={subjectByLocale?.[language.locale] ?? ''}
              previewPdfWidth={A4_WIDTH_CM}
              previewPdfPerTicketHeight={
                pdfPerTicketHeightByLocale?.[language.locale]
              }
            />
          </div>
        )
      })}
    </div>
  )
}

interface TemplatesProps {
  setSelectedTemplate: (s: string) => void
  selectedTemplate: string
  allLanguages: Languages[]
  variables: Variable[]
}

export const Templates = (props: TemplatesProps) => {
  const { hasEditPermission } = useCheckPermission({ page: 'SETTINGS' })
  const { setSelectedTemplate, selectedTemplate, allLanguages, variables } =
    props
  const [previewTemplate, setPreviewTemplate] = useState<string>('')
  const [isUploading, setIsUploading] = useState<boolean>(true)
  const [showPreview, setShowPreview] = useState<boolean>(false)
  const [currentTemplate, setCurrentTemplate] = useState<Template>()
  const [designType, setDesignType] = useState<'default' | 'custom'>('default')
  const [allTemplates, setAllTemplates] = useState<
    { name: string; items: ITemplates[] }[]
  >([])
  const [velocityByLocale, setVelocityByLocale] = useState<
    Record<string, EmailTemplateVelocity>
  >({})
  const [customTemplateByLocale, setCustomTemplateByLocale] = useState<
    Record<string, string>
  >({})
  const [customSubjectByLocale, setCustomSubjectByLocale] = useState<
    Record<string, string>
  >({})
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const removeDuplicates = (arr: any) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return arr.filter((item: any, index: number) => arr.indexOf(item) === index)
  }

  useEffect(() => {
    // prettier-ignore
    (async () => {
      setIsUploading(true)
      try {
        const resp = await getAllEmailTemplates()
        if (resp.status === 200) {
          const categories = removeDuplicates(map(resp.data, d => d.category))
          const temp = map(categories, cat => {
            return {
              name: cat,
              items: filter(resp.data, d => d.category === cat)
            }
          })
          setAllTemplates(temp)
          setSelectedTemplate(resp.data[0].id)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setIsUploading(false)
      }
    })()
  }, [])

  const schema = yup.object().shape({
    id: yup.string().required(),
    name: yup.string().required(),
    category: yup.string().required(),
    type: yup
      .string()
      .oneOf([
        'NEWSLETTER_SUBSCRIPTION_CONFIRMATION',
        'USER_EMAIL_CONFIRMATION',
        'YOUR_TICKETS',
        'PAYMENT_FAILED',
        'REFUND',
        'INVITE_BATCH',
        'INVITE_BATCH_MAX_10_INVITES',
        'INVITE',
        'CHECKIN_SUCCESSFUL_PAYMENT',
        'CONTACT_FORM',
        'CANCEL_CHECKIN',
        'FREE_CHECKIN_REMINDER',
        'PAID_CHECKIN_REMINDER',
        'CONFIRMATION_CODE',
        'TOPUP_SUCCESSFUL_PAYMENT',
        'TOPUP_FAILED_PAYMENT',
        'TOPUP_COINS_EXPIRATION',
        'REFUND_TOPUP',
        'EXTRAS_DELIVERY_SUCCESSFUL_PAYMENT',
        'EXTRAS_DELIVERY_FAILED_PAYMENT',
        'EXTRAS_DELIVERY_AWB_DETAILS',
        'REFERRAL_BONUS',
        'ON_SITE_ORDER_ATTACHMENT',
        'INVITE_TICKET_REDEEM_INFO_ATTACHMENT',
        'NEW_TICKET',
        'INSTALMENTS_PAYMENT_SUCCESS',
        'INSTALMENTS_PAYMENT_FAILED',
        'INSTALMENTS_PAYMENT_REMINDER',
      ])
      .required(),
    texts: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.string().required(),
          key: yup.string().required(),
          optional: yup.boolean().required(),
          displayable: yup.boolean().required(),
          translations: yup
            .array()
            .of(
              yup.object().shape({
                id: yup.string().required(),
                locale: yup.string().required(),
                text: yup.string().required(),
              }),
            )
            .when('displayable', {
              is: (val: boolean) => val,
              then: (schema) =>
                schema
                  .min(1, 'At least one translation is required')
                  .required(),
              otherwise: (schema) => schema.min(0).optional(),
            }),
        }),
      )
      .required(),
  })

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    control,
    watch,
  } = useForm<IFormValues>({
    mode: 'onChange',
    resolver: yupResolver<IFormValues>(schema),
  })

  useEffect(() => {
    // prettier-ignore
    (async () => {
      setIsUploading(true)
      try {
        if (selectedTemplate !== '') {
          const resp = await getEmailTemplateById(selectedTemplate)
          if (resp.status === 200) {
            setCurrentTemplate(resp.data)
            setValue('id', resp.data.id)
            setValue('name', resp.data.name)
            setValue('category', resp.data.category)
            setValue('type', resp.data.type)
            setTimeout(() => {
              setValue('texts', resp.data.texts)
            }, 200)
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setIsUploading(false)
      }
    })()
  }, [selectedTemplate])

  const { fields } = useFieldArray({
    control,
    name: 'texts',
  })

  useEffect(() => {
    if (!selectedTemplate || allLanguages.length === 0) return
    ;(async () => {
      setIsUploading(true)
      try {
        const results = await Promise.all(
          allLanguages.map((lang) =>
            getEmailTemplateVelocity(selectedTemplate, lang.locale).then(
              (r) => ({ locale: lang.locale, data: r.data }),
            ),
          ),
        )
        const byLocale: Record<string, EmailTemplateVelocity> = {}
        const templates: Record<string, string> = {}
        const subjects: Record<string, string> = {}
        let hasCustomTemplate = false
        results.forEach(({ locale, data }) => {
          byLocale[locale] = data
          templates[locale] = data.customTemplate || data.defaultTemplate || ''
          subjects[locale] = data.customSubject || ''
          if (data.customTemplate) hasCustomTemplate = true
        })
        setVelocityByLocale(byLocale)
        setCustomTemplateByLocale(templates)
        setCustomSubjectByLocale(subjects)
        if (hasCustomTemplate) {
          setDesignType('custom')
        } else {
          setDesignType('default')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      } finally {
        setIsUploading(false)
      }
    })()
  }, [selectedTemplate, allLanguages])

  const setCustomTemplateForLocale = (locale: string, template: string) => {
    setCustomTemplateByLocale((prev) => ({ ...prev, [locale]: template }))
  }

  const setCustomSubjectForLocale = (locale: string, subject: string) => {
    setCustomSubjectByLocale((prev) => ({ ...prev, [locale]: subject }))
  }

  const submitCustom = async () => {
    setIsUploading(true)
    try {
      const updated = await Promise.all(
        Object.entries(customTemplateByLocale).map(
          async ([locale, template]) => {
            const baseHeight = await measureA4HeightCm(template)
            const perTicketHeight =
              velocityByLocale[locale]?.customPdfPerTicketHeight ?? 0
            const r = await updateEmailTemplateVelocity(
              selectedTemplate,
              {
                template,
                customSubject: customSubjectByLocale[locale] || '',
                customPdfWidth: A4_WIDTH_CM,
                customPdfBaseHeight: baseHeight,
                ...(perTicketHeight !== 0 && {
                  customPdfPerTicketHeight: perTicketHeight,
                }),
              },
              locale,
            )
            return { locale, data: r.data }
          },
        ),
      )
      const byLocale = { ...velocityByLocale }
      updated.forEach(({ locale, data }) => {
        byLocale[locale] = data
      })
      setVelocityByLocale(byLocale)
      setIsError(false)
      setShowMessageBox(true)
      setMessage('Update Successful')
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setIsUploading(false)
    }
  }

  const handleUseDefaultLocale = async (locale: string) => {
    setIsUploading(true)
    try {
      await deleteEmailTemplateVelocity(selectedTemplate, locale)
      const r = await getEmailTemplateVelocity(selectedTemplate, locale)
      setVelocityByLocale((prev) => ({ ...prev, [locale]: r.data }))
      setCustomTemplateByLocale((prev) => ({
        ...prev,
        [locale]: r.data.defaultTemplate || '',
      }))
      setCustomSubjectByLocale((prev) => ({
        ...prev,
        [locale]: r.data.customSubject || '',
      }))
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setIsUploading(false)
    }
  }

  const submit = async (values: IFormValues) => {
    // prettier-ignore
    (async () => {
      setIsUploading(true)
      try {
        const payload: Template = {
          id: values.id,
          name: values.name,
          category: values.category,
          type: values.type,
          texts: map(values.texts, text => {
            return ({
              id: text.id,
              key: text.key,
              optional: text.optional,
              displayable: text.displayable,
              translations: map(text.translations, trans => {
                return ({
                  id: trans.id,
                  locale: trans.locale,
                  text: trans.text,
                })
              })
            })
          })
        }
        const resp = await updateEmailTemplateById(values.id, payload)
        if (resp.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setIsUploading(false)
      }
    })()
  }

  const handlePreview = () => {
    // prettier-ignore
    (async () => {
      setIsUploading(true)
      try {
        const resp = await getPreviews(selectedTemplate)
        if (resp.status === 200) {
          setPreviewTemplate(`<div
                  style="
                  width: 100%;
                  margin: auto;
                  "
                  >${resp.data}</div>`)
          setShowPreview(true)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setIsUploading(false)
      }
    })()
  }

  if (isUploading) {
    return <Loader />
  }

  const templateGroups: SelectOptionGroup[] = allTemplates.map((template) => ({
    label: template.name,
    options: template.items.map((item) => ({
      value: item.id,
      label: item.name,
    })),
  }))

  return (
    <div className="max-h-[calc(100vh-215px)] overflow-y-auto">
      <div className="flex flex-col gap-2 bg-gradient-4 p-5 rounded-2xl border border-monochrome-120">
        <div className="flex items-center gap-2">
          <SelectField
            outline
            placeholder="Select Template"
            value={selectedTemplate}
            onValueChange={setSelectedTemplate}
            optionGroups={templateGroups}
            wrapperClassName="flex-1"
          />
          {designType !== 'custom' && (
            <Button
              variant="secondary"
              onClick={handlePreview}
              size="sm"
              className="h-[36px]"
            >
              Preview Template
              <Icon icon="eye" width="18" height="18" viewBox="0 0 18 18" />
            </Button>
          )}
        </div>
        <SelectField
          outline
          label="Design Type"
          placeholder="Select Design Type"
          value={designType}
          onValueChange={(value) =>
            setDesignType(value as 'default' | 'custom')
          }
          options={[
            { value: 'default', label: 'Default' },
            { value: 'custom', label: 'Custom' },
          ]}
          wrapperClassName="flex-1"
        />
      </div>
      <form id="template-form" onSubmit={handleSubmit(submit)}>
        {designType === 'custom' ? (
          <>
            <CustomHtmlSection
              title="Subject"
              fieldName="custom-subject"
              control={control}
              variables={[]}
              allLanguages={allLanguages}
              disabled={!hasEditPermission}
              valueByLocale={customSubjectByLocale}
              setValueForLocale={setCustomSubjectForLocale}
            />
            <CustomHtmlSection
              title="Content HTML"
              fieldName="custom-template"
              control={control}
              variables={variables}
              allLanguages={allLanguages}
              disabled={!hasEditPermission}
              valueByLocale={customTemplateByLocale}
              setValueForLocale={setCustomTemplateForLocale}
              onUseDefault={handleUseDefaultLocale}
              showPreview
              showLegend
              templateId={selectedTemplate}
              subjectByLocale={customSubjectByLocale}
              pdfPerTicketHeightByLocale={Object.fromEntries(
                Object.entries(velocityByLocale).map(([locale, v]) => [
                  locale,
                  v.customPdfPerTicketHeight ?? 0,
                ]),
              )}
            />
          </>
        ) : (
          fields.map((field, index) => (
            <EmailTemplateItem
              control={control}
              field={field}
              key={field.id}
              index={index}
              variables={variables}
              setValue={setValue}
              watch={watch}
              errors={errors}
              allLanguages={allLanguages}
              disabled={!hasEditPermission}
            />
          ))
        )}
        <div className="flex justify-end w-full gap-2 mt-4 [&>*]:w-[154px]">
          <Button
            onClick={() => {
              reset()
            }}
            variant="secondary"
            data-dismiss="modal"
            disabled={!hasEditPermission}
            type="button"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={() => {
              if (designType === 'custom') {
                submitCustom()
              } else {
                handleSubmit(submit)()
              }
            }}
            disabled={!hasEditPermission}
          >
            Save
          </Button>
        </div>
      </form>

      <PreviewTemplate
        title={currentTemplate?.name || ''}
        template={previewTemplate}
        visible={showPreview}
        setVisible={setShowPreview}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </div>
  )
}
